// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';

import { getMetricsServiceOwnerBaseUrl } from './getMetricsServiceOwnerBaseUrl.tsx';
import { getSourceAppId } from './getSourceAppId.tsx';
import { getSyntheticTrafficHeader } from './getSyntheticTrafficHeader.tsx';
import type { CreateSemanticModelResourceArgs } from './CreateSemanticModelResourceArgs.tsx';

type FetchAvpSemanticModelArgs = CreateSemanticModelResourceArgs & {
	activationId: string;
	cloudId: string;
	userInfo: { localTimeZone: string; locale: string };
};

type SemanticModelResponseLike = {
	products?: Array<{ name?: string } & Record<string, unknown>>;
} & Record<string, unknown>;

/**
 * Default `products[]` ordering applied to the `/v3/metrics/semantic_model`
 * response when `platform_avp_metric_sorting` is ON and the caller did not
 * supply its own `desiredOrder`. Matches the legacy AVP order from
 * `chart-library-form/.../metricField.tsx` so non-Assets Jira routes keep
 * the same dropdown order they had before the gate flipped.
 */
const DEFAULT_PRODUCT_ORDER: readonly string[] = ['jira', 'goals', 'devops', 'xray'];

/**
 * Sort `data.products` so they appear in `desiredOrder`. Products whose
 * `name` is not in `desiredOrder` are placed at the end and keep their
 * original relative order (`Array.prototype.sort` is stable). Pure (returns
 * a new object; does not mutate input).
 *
 * Consumers pass the FULL ordered list they want
 */
const sortProductsByDesiredOrder = (
	data: SemanticModelResponseLike,
	desiredOrder: readonly string[],
): SemanticModelResponseLike => {
	if (!data || !Array.isArray(data.products) || data.products.length === 0) return data;
	if (desiredOrder.length === 0) return data;
	const indexOf = (name: string | undefined): number => {
		if (name == null) return Infinity;
		const i = desiredOrder.indexOf(name);
		return i === -1 ? Infinity : i;
	};
	const products = data.products.slice().sort((a, b) => indexOf(a?.name) - indexOf(b?.name));
	return { ...data, products };
};

export async function fetchAvpSemanticModel({
	activationId,
	cloudId,
	userInfo,
	hostingProduct,
	location,
	products,
	subProducts,
	desiredOrder,
	// Until `MetricsApiResponse` type is available from an isolated AVP package, we are using `any`
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
}: FetchAvpSemanticModelArgs): Promise<any> {
	const resolvedProducts = typeof products === 'function' ? products() : products;
	const resolvedSubProducts = typeof subProducts === 'function' ? subProducts() : subProducts;
	const resolvedLocation = typeof location === 'function' ? location() : location;
	const metricsBaseUrl = getMetricsServiceOwnerBaseUrl(hostingProduct, cloudId, activationId);
	const url = `${metricsBaseUrl}/v3/metrics/semantic_model`;

	const sharedHeaders: Record<string, string> = {
		'X-Source-App-Id': getSourceAppId(hostingProduct, resolvedLocation),
		'cloud-id': cloudId,
		'workspace-id': activationId,
		...getSyntheticTrafficHeader(),
	};

	const params = new URLSearchParams();
	if (resolvedLocation) {
		params.set('locations', resolvedLocation);
	}
	if (resolvedProducts && resolvedSubProducts) {
		if (resolvedProducts.length > 0) {
			params.set('products', resolvedProducts.join(','));
		}
		if (resolvedSubProducts.length > 0) {
			params.set('subProducts', resolvedSubProducts.join(','));
		}
	}
	if (userInfo.localTimeZone) {
		params.set('localTimeZone', userInfo.localTimeZone);
	}
	if (userInfo.locale) {
		params.set('locale', userInfo.locale);
	}
	const queryString = params.toString();
	const getUrl = queryString ? `${url}?${queryString}` : url;
	const response = await fetch(getUrl, {
		method: 'GET',
		headers: sharedHeaders,
	});
	if (response.status === 414) {
		fireErrorAnalytics({
			meta: {
				id: 'fetchAvpSemanticModelUriTooLong',
				packageName: 'jiraRouterResourcesVizPlatform',
				teamName: 'viz-platform',
			},
			error: new Error('fetchAvpSemanticModel: HTTP 414 URI Too Long on GET request'),
			attributes: {
				urlLength: getUrl.length,
				queryStringLength: queryString.length,
				hostingProduct,
				location: resolvedLocation ?? '',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	}

	if (!response.ok) {
		throw new Error(`fetchAvpSemanticModel failed: ${response.status}`);
	}

	const data: SemanticModelResponseLike = await response.json();
	if (fg('platform_avp_metric_sorting')) {
		return sortProductsByDesiredOrder(data, desiredOrder ?? DEFAULT_PRODUCT_ORDER);
	}
	return data;
}
